/**
 * Parser for host_upgrade operation stage markers from OperationLogLine stream.
 *
 * Stage markers are emitted by HostSSHWorker in the format:
 *   === STAGE: <name> START ===
 *   === STAGE: <name> END (exit=<N>) ===
 *   === STAGE: <name> SKIPPED reason="<text>" ===
 *
 * CRITICAL: OperationLogLine.message may be multi-line. HostSSHWorker.Work calls
 * AppendLog ONCE with the full SSH session output containing embedded \n characters.
 * The parser MUST split each line.message on \n first, then apply the regex per
 * raw text line. Without this split, the anchored regex matches nothing.
 */
import type { OperationLogLine } from '@/core/infra_types'

// Stage names as defined by the backend bash chain (U2)
export type StageName = 'precheck' | 'apt' | 'postinst' | 'verify'

// Stage lifecycle states
export type StageStatus = 'pending' | 'running' | 'success' | 'failed' | 'skipped'

export interface StageStatusEntry {
  name: StageName
  status: StageStatus
  exitCode?: number
  reason?: string
  startedAtIdx?: number  // index into the flattened raw-text-line array
  endedAtIdx?: number
}

// Canonical stage order for output
const CANONICAL_STAGES: readonly StageName[] = ['precheck', 'apt', 'postinst', 'verify'] as const

// Anchored regex for stage markers, applied per raw text line
// Must match the backend's marker format exactly (case-sensitive)
const STAGE_MARKER_REGEX = /^=== STAGE: (precheck|apt|postinst|verify) (START|END \(exit=(\d+)\)|SKIPPED reason="([^"]*)") ===$/

/**
 * Parse stage markers from operation log lines.
 *
 * @param lines - OperationLogLine[] from useOperationLogs hook
 * @returns Array of 4 StageStatusEntry records in canonical order (precheck, apt, postinst, verify)
 */
export function parseStageMarkers(lines: OperationLogLine[]): StageStatusEntry[] {
  // Initialize result with all stages pending
  const statusMap: Record<StageName, StageStatusEntry> = {
    precheck: { name: 'precheck', status: 'pending' },
    apt: { name: 'apt', status: 'pending' },
    postinst: { name: 'postinst', status: 'pending' },
    verify: { name: 'verify', status: 'pending' },
  }

  // Track which warnings we've already emitted to avoid spam
  const warnedEndWithoutStart = new Set<StageName>()

  // Global line index for tracking line ranges
  let globalLineIdx = 0

  // Step 1: Flatten - split each OperationLogLine.message on \n into raw text lines
  for (const logLine of lines) {
    const rawTextLines = logLine.message.split('\n')

    // Step 2: Apply regex to each raw text line
    for (const rawLine of rawTextLines) {
      const match = STAGE_MARKER_REGEX.exec(rawLine)

      if (match) {
        const stageName = match[1] as StageName
        const action = match[2]
        const entry = statusMap[stageName]

        if (action === undefined) {
          // Should not happen with valid regex, but TypeScript needs the guard
          continue
        } else if (action === 'START') {
          // START: transition from pending -> running (or stay running on duplicate)
          if (entry.status === 'pending') {
            entry.status = 'running'
            entry.startedAtIdx = globalLineIdx
          } else if (entry.status === 'running') {
            // Duplicate START - keep running, don't warn (it's harmless)
          }
          // Ignore START if already terminal (success/failed/skipped)
        } else if (action.startsWith('END')) {
          // Parse exit code
          const exitCodeStr = match[3]
          const exitCode = exitCodeStr !== undefined ? parseInt(exitCodeStr, 10) : 0

          if (entry.status === 'running') {
            // Normal transition: running -> success/failed
            entry.status = exitCode === 0 ? 'success' : 'failed'
            entry.exitCode = exitCode
            entry.endedAtIdx = globalLineIdx
          } else if (entry.status === 'pending') {
            // END without START - warn and ignore
            if (!warnedEndWithoutStart.has(stageName)) {
              console.warn(`[parseStageMarkers] END without START for stage: ${stageName}`)
              warnedEndWithoutStart.add(stageName)
            }
          }
          // Ignore END if already terminal
        } else if (action.startsWith('SKIPPED')) {
          // Parse reason
          const reason = match[4] ?? ''

          // SKIPPED can come after START (precheck started, then decided to skip)
          // or directly from pending
          if (entry.status === 'pending' || entry.status === 'running') {
            entry.status = 'skipped'
            entry.reason = reason
            entry.endedAtIdx = globalLineIdx
          }
          // Ignore if already terminal
        }
      }

      globalLineIdx++
    }
  }

  // Return in canonical order
  return CANONICAL_STAGES.map(name => statusMap[name])
}
